import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axiosInstance from '../../../axiosConfig';
import axios from 'axios';
import styles from './AgenticChat.module.css';
import SummaryDisplayChat from '../MicroProbe/Companies/SummaryDisplay_Chat';
import { renderCategoryCharts } from '../MicroProbe/Companies/InsightExplorerChartUtils';

const MODES = {
  numbers: {
    id: 'numbers',
    icon: '⚡',
    label: 'Data Analysis',
    title: 'Data Analysis',
    description: 'Fast answers backed by live market data and analysis.',
    placeholder: 'Ask about a stock, currency pair, crypto, and more...',
    loadingText: 'Analyzing',
  },
  insights: {
    id: 'insights',
    icon: '🔬',
    label: 'Insights',
    title: 'Deeper Research Insights',
    description: 'Comprehensive analysis with narrative context and interactive charts.',
    placeholder: 'Ask for a deep dive into markets, economies, themes...',
    loadingText: 'Researching',
  },
};

const SPEED_NAMES = ['Quick', 'Fast', 'Balanced', 'Detailed', 'Thorough'];

const AgenticChat = ({ fixedMode }) => {
  const [mode, setMode] = useState(fixedMode || 'numbers');
  const [userMessage, setUserMessage] = useState('');
  const [chatHistory, setChatHistory] = useState([]);
  const [displayChatHistory, setDisplayChatHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [speed, setSpeed] = useState(2); // 0 = fastest, 4 = slowest
  const cancelToken = useRef(null);
  const lastRequestTime = useRef(0);
  const chatWindowRef = useRef(null);
  const inputRef = useRef(null);
  const MIN_REQUEST_INTERVAL = 2000;

  const currentMode = MODES[mode];

  // ── Chart helpers (insights mode) ─────────────────────────────────────────

  const getDefaultDateRange = (chartData) => {
    if (!chartData || !chartData.dates || chartData.dates.length === 0) {
      const today = new Date();
      const oneYearAgo = new Date(today);
      oneYearAgo.setFullYear(today.getFullYear() - 1);
      return {
        from: oneYearAgo.toISOString().split('T')[0],
        to: today.toISOString().split('T')[0],
      };
    }
    const latestDate = new Date(Math.max(...chartData.dates.map(d => new Date(d))));
    const oneYearAgo = new Date(latestDate);
    oneYearAgo.setFullYear(latestDate.getFullYear() - 1);
    const earliestAvailable = new Date(Math.min(...chartData.dates.map(d => new Date(d))));
    const fromDate = earliestAvailable > oneYearAgo ? earliestAvailable : oneYearAgo;
    return {
      from: fromDate.toISOString().split('T')[0],
      to: latestDate.toISOString().split('T')[0],
    };
  };

  const getLastAssistantChartIndex = (history) => {
    for (let i = history.length - 1; i >= 0; i--) {
      if (history[i].role === 'assistant' && history[i].chartData) return i;
    }
    return -1;
  };

  const handleLatestDateRangeChange = (field, value) => {
    setDisplayChatHistory(prev => {
      const idx = getLastAssistantChartIndex(prev);
      if (idx === -1) return prev;
      const updated = [...prev];
      const msg = updated[idx];
      const currentRange = msg.chartDateRange || getDefaultDateRange(msg.chartData);
      updated[idx] = { ...msg, chartDateRange: { ...currentRange, [field]: value } };
      return updated;
    });
  };

  const resetLatestDateRange = () => {
    setDisplayChatHistory(prev => {
      const idx = getLastAssistantChartIndex(prev);
      if (idx === -1) return prev;
      const updated = [...prev];
      const msg = updated[idx];
      updated[idx] = { ...msg, chartDateRange: getDefaultDateRange(msg.chartData) };
      return updated;
    });
  };

  // ── Scroll ─────────────────────────────────────────────────────────────────

  useEffect(() => {
    if (chatWindowRef.current) {
      chatWindowRef.current.scrollTop = chatWindowRef.current.scrollHeight;
    }
  }, [displayChatHistory, loading]);

  // ── Mode switching ─────────────────────────────────────────────────────────

  const handleModeSwitch = (newMode) => {
    if (newMode === mode) return;
    setMode(newMode);
    setChatHistory([]);
    setDisplayChatHistory([]);
    setError('');
    setUserMessage('');
    lastRequestTime.current = 0;
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  // ── Submit ─────────────────────────────────────────────────────────────────

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!userMessage.trim()) return;

    const now = Date.now();
    if (now - lastRequestTime.current < MIN_REQUEST_INTERVAL) {
      const wait = Math.ceil((MIN_REQUEST_INTERVAL - (now - lastRequestTime.current)) / 1000);
      setError(`Please wait ${wait} second${wait > 1 ? 's' : ''}`);
      return;
    }
    lastRequestTime.current = now;

    setError('');
    setLoading(true);

    const newUserMessage = { role: 'user', content: userMessage };
    const currentMessage = userMessage;
    setChatHistory(prev => [...prev, newUserMessage]);
    setDisplayChatHistory(prev => [...prev, newUserMessage]);
    setUserMessage('');

    if (cancelToken.current) {
      cancelToken.current.cancel('New request started');
    }
    cancelToken.current = axios.CancelToken.source();

    try {
      if (mode === 'numbers') {
        const response = await axiosInstance.post('/chat_with_numbers/', {
          customer_request: currentMessage,
          chat_history: chatHistory,
          speed: speed,
          context: {},
        }, { cancelToken: cancelToken.current.token });

        if (response.data.error) throw new Error(response.data.error);

        const assistantMessage = {
          role: 'assistant',
          content: response.data.response,
          images: response.data.new_images || [],
        };
        setChatHistory(prev => [...prev, { role: 'assistant', content: response.data.response }]);
        setDisplayChatHistory(prev => [...prev, assistantMessage]);

      } else {
        const response = await axiosInstance.post('/chat_with_insight_explorer/', {
          customer_request: currentMessage,
          chat_history: chatHistory,
        }, { cancelToken: cancelToken.current.token });

        let analysisContent;
        let chartData = null;

        if (typeof response.data === 'string') {
          analysisContent = response.data;
        } else if (response.data && typeof response.data === 'object') {
          analysisContent = response.data.analysis || response.data.response || 'No analysis available';
          chartData = response.data.chart_data || null;
          if (!response.data.analysis && !response.data.response && !response.data.error) {
            analysisContent = 'Server processing issue. Please try again in a moment.';
          }
        } else {
          analysisContent = 'No response received';
        }

        setChatHistory(prev => [...prev, { role: 'assistant', content: analysisContent }]);
        setDisplayChatHistory(prev => [...prev, {
          role: 'assistant',
          content: analysisContent,
          chartData: chartData || null,
          chartDateRange: chartData ? getDefaultDateRange(chartData) : undefined,
        }]);
      }

    } catch (err) {
      if (axios.isCancel(err)) return;
      if (err.response?.status === 429) {
        setError('Please wait a moment before sending another request');
      } else {
        setError(err.response?.data?.error || err.message || 'An error occurred');
      }
      setChatHistory(prev => prev.slice(0, -1));
      setDisplayChatHistory(prev => prev.slice(0, -1));
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setChatHistory([]);
    setDisplayChatHistory([]);
    setError('');
    setUserMessage('');
    lastRequestTime.current = 0;
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const lastAssistantIndex = (() => {
    for (let i = displayChatHistory.length - 1; i >= 0; i--) {
      if (displayChatHistory[i].role === 'assistant') return i;
    }
    return -1;
  })();

  const hasMessages = displayChatHistory.length > 0;

  return (
    <div className={`${styles.container} ${fixedMode ? styles.embedded : ''}`}>

      {/* ── Header ── */}
      {(!fixedMode || hasMessages) && (
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            {!fixedMode && (
              <>
                <span className={styles.headerLogo}>◆</span>
                <h3>Ask Fina</h3>
              </>
            )}
            {hasMessages && (
              <span className={`${styles.modeBadge} ${mode === 'insights' ? styles.modeBadgeInsights : ''}`}>
                {currentMode.icon} {currentMode.label}
              </span>
            )}
          </div>
          {hasMessages && (
            <button onClick={handleClear} className={styles.clearButton}>
              New chat
            </button>
          )}
        </div>
      )}

      {/* ── Chat window ── */}
      <div
        className={`${styles.chatWindow} ${!hasMessages ? styles.chatWindowEmpty : ''}`}
        ref={chatWindowRef}
      >
        {!hasMessages ? (
          <div className={styles.emptyState}>
            <p className={styles.emptyPrompt}>
              {mode === 'insights' ? 'What are we researching?' : 'What are we exploring today?'}
            </p>

            {/* Mode selector */}
            {!fixedMode && (
              <div className={styles.modeSegment}>
                {Object.values(MODES).map(m => (
                  <button
                    key={m.id}
                    className={`${styles.modeSegmentBtn} ${mode === m.id ? styles.modeSegmentActive : ''}`}
                    onClick={() => handleModeSwitch(m.id)}
                  >
                    <span>{m.icon}</span>
                    <span>{m.label}</span>
                  </button>
                ))}
              </div>
            )}
            <p key={mode} className={styles.modeDesc}>{currentMode.description}</p>

            {/* Input */}
            <form onSubmit={handleSubmit} data-mode={mode} className={styles.inputForm}>
              <div className={styles.inputTopRow}>
                <textarea
                  ref={inputRef}
                  value={userMessage}
                  onChange={(e) => setUserMessage(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder={currentMode.placeholder}
                  rows={1}
                  className={styles.input}
                  disabled={loading}
                />
              </div>
              <div
                className={styles.inputBottomRow}
                style={mode === 'insights' ? { justifyContent: 'flex-end' } : undefined}
              >
                {mode === 'numbers' && (
                  <div className={styles.speedSection}>
                    <span className={styles.speedSectionLabel}>Thinking effort</span>
                    <span className={styles.speedLabel}>Fast</span>
                    <div className={styles.speedGrids}>
                      {[0, 1, 2, 3, 4].map(i => (
                        <div
                          key={i}
                          role="button"
                          tabIndex={0}
                          className={`${styles.speedCell} ${i <= speed ? styles.speedCellActive : ''} ${i === speed ? styles.speedCellSelected : ''}`}
                          onClick={() => setSpeed(i)}
                          onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && setSpeed(i)}
                          aria-label={`Speed: ${SPEED_NAMES[i]}`}
                        />
                      ))}
                    </div>
                    <span className={styles.speedLabel}>Slow</span>
                  </div>
                )}
                <button
                  type="submit"
                  disabled={loading || !userMessage.trim()}
                  className={styles.submitButton}
                />
              </div>
            </form>
          </div>
        ) : (
          displayChatHistory.map((message, index) => (
            <div
              key={index}
              className={`${styles.message} ${styles[message.role]}`}
            >
              <div className={styles.messageContent}>
                {message.role === 'user' ? (
                  <span>{message.content}</span>
                ) : (
                  <>
                    <SummaryDisplayChat content={message.content} />

                    {message.images?.map((imgUrl, imgIndex) => (
                      <div key={imgIndex} className={styles.imageContainer}>
                        <img src={imgUrl} alt={`Chart ${imgIndex + 1}`} className={styles.chartImage} />
                      </div>
                    ))}

                    {message.chartData && (
                      <div className={styles.chartSection}>
                        <div className={styles.chartHeader}>
                          <strong className={styles.chartHeaderLabel}>Charts</strong>
                          {index === lastAssistantIndex && (
                            <div className={styles.dateRangeControls}>
                              <div className={styles.dateRangeField}>
                                <label className={styles.dateRangeLabel}>From:</label>
                                <input
                                  type="date"
                                  value={message.chartDateRange?.from || ''}
                                  onChange={(e) => handleLatestDateRangeChange('from', e.target.value)}
                                  className={styles.dateInput}
                                />
                              </div>
                              <div className={styles.dateRangeField}>
                                <label className={styles.dateRangeLabel}>To:</label>
                                <input
                                  type="date"
                                  value={message.chartDateRange?.to || ''}
                                  onChange={(e) => handleLatestDateRangeChange('to', e.target.value)}
                                  className={styles.dateInput}
                                />
                              </div>
                              <button
                                type="button"
                                onClick={resetLatestDateRange}
                                className={styles.dateResetButton}
                              >
                                Reset
                              </button>
                            </div>
                          )}
                        </div>
                        {renderCategoryCharts(message.chartData, message.chartDateRange, styles)}
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          ))
        )}

        {loading && (
          <div className={`${styles.message} ${styles.assistant}`}>
            <div className={styles.messageRole}>Fina</div>
            <div className={styles.messageContent}>
              <div className={styles.loading}>
                <span>{currentMode.loadingText}</span>
                <span className={styles.dots}></span>
              </div>
            </div>
          </div>
        )}
      </div>

      {error && <div className={styles.error}>{error}</div>}

      {hasMessages && (
        <form onSubmit={handleSubmit} data-mode={mode} className={styles.inputForm}>
          <div className={styles.inputTopRow}>
            <textarea
              ref={inputRef}
              value={userMessage}
              onChange={(e) => setUserMessage(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={currentMode.placeholder}
              rows={1}
              className={styles.input}
              disabled={loading}
            />
          </div>
          <div
            className={styles.inputBottomRow}
            style={mode === 'insights' ? { justifyContent: 'flex-end' } : undefined}
          >
            {mode === 'numbers' && (
              <div className={styles.speedSection}>
                <span className={styles.speedSectionLabel}>Thinking effort</span>
                <span className={styles.speedLabel}>Fast</span>
                <div className={styles.speedGrids}>
                  {[0, 1, 2, 3, 4].map(i => (
                    <div
                      key={i}
                      role="button"
                      tabIndex={0}
                      className={`${styles.speedCell} ${i <= speed ? styles.speedCellActive : ''} ${i === speed ? styles.speedCellSelected : ''}`}
                      onClick={() => setSpeed(i)}
                      onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && setSpeed(i)}
                      aria-label={`Speed: ${SPEED_NAMES[i]}`}
                    />
                  ))}
                </div>
                <span className={styles.speedLabel}>Slow</span>
              </div>
            )}
            <button
              type="submit"
              disabled={loading || !userMessage.trim()}
              className={styles.submitButton}
            />
          </div>
        </form>
      )}

      <div className={styles.backLinkContainer}>
        <Link to="/">Back</Link>
      </div>
    </div>
  );
};

export default AgenticChat;
